import moment from 'moment';
import { observable } from 'mobx';
import { pick, get, difference } from 'lodash';
import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import { withTheme } from 'styled-components';

import Page from 'app/components/page/Page';
import { getHashForObject, getQueryForHash } from 'app/stores/query/urlHash';
import { asyncDebounce } from 'core/util';
import { FormComponent, Field, InputGroup, Select, LookbackDateRange, SubmitButton } from 'core/form';
import { Box, Flex, Label, Button, Container, Spinner, Suspense, Text, Card, Tag } from 'core/components';
import withHybridTopoSettings from 'app/views/hybrid/maps/components/settingsToolbar/withHybridTopoSettings';
import MapSearchAwsSubscriptionsPanel from 'app/views/hybrid/maps/components/settingsToolbar/mapSearch/AwsAccountsPanel';
import MapSearchAzureSubscriptionsPanel from 'app/views/hybrid/maps/components/settingsToolbar/mapSearch/AzureSubscriptionsPanel';

import { convertToReadableId } from './utils';
import { SourceFieldType as AwsSourceFieldType, TargetFieldType as AwsTargetFieldType } from './aws/constants';
import { SourceFieldType as AzureSourceFieldType, TargetFieldType as AzureTargetFieldType } from './azure/constants';

const options = {
  name: 'Pathfinder Report'
};

@withHybridTopoSettings
@inject('$clouds', '$hybridMap')
@withRouter
@withTheme
@observer
export default class CreateConnectivityReport extends Component {
  cloud = null;

  @observable
  connectivityReportModel = null;

  state = {
    selectedTime: {
      lookbackSeconds: 21600,
      startDate: null,
      endDate: null
    },
    nameUpdatedManually: false,
    loadingMessage: 'Initializing...',
    hasEndpointOptions: true
  };

  get cloudMapCollection() {
    const { match, $hybridMap } = this.props;

    const cloud = match?.params?.cloud ?? 'aws';

    if (cloud === 'aws') {
      return $hybridMap.awsCloudMapCollection;
    }

    if (cloud === 'azure') {
      return $hybridMap.azureCloudMapCollection;
    }

    return $hybridMap.awsCloudMapCollection;
  }

  async intializeDefaultValues() {
    const { location, match } = this.props;

    const cloud = match?.params?.cloud ?? 'aws';

    if (location.state) {
      const { src_type, dst_type, src, dst } = location.state || {};
      return {
        src,
        dst,
        cloud,
        src_type,
        dst_type,
        name: src && dst && `Connectivity between ${src} and ${dst}`
      };
    }

    if (location.search) {
      const params = new URLSearchParams(location.search);
      const hash = params.get('hash');

      if (hash) {
        const query = await getQueryForHash(hash);
        const { src_type, dst_type, src, dst } = query;
        return {
          src,
          dst,
          cloud,
          src_type,
          dst_type,
          name: src && dst && `Connectivity between ${src} and ${dst}`
        };
      }
    }

    return null;
  }

  get isAwsPage() {
    return this.cloud === 'aws';
  }

  get isAzurePage() {
    return this.cloud === 'azure';
  }

  handleAwsAccountsChange = ({ awsAccountIds = [] }) => {
    const diff = difference(this.cloudMapCollection.currentLoadedAccountIds, awsAccountIds);
    const reverseDiff = difference(awsAccountIds, this.cloudMapCollection.currentLoadedAccountIds);

    if ([...diff, ...reverseDiff].length === 0) {
      // no change in accounts selection
      return;
    }

    this.setState({ loadingMessage: 'Loading AWS Topology...' });
    this.cloudMapCollection
      .fetchConnectivityCheckerTopology({
        force: true,
        data: { accountIds: awsAccountIds }
      })
      .then(() => this.intializeDefaultValues())
      .then((defaultValues) => {
        this.connectivityReportModel.set(defaultValues ?? {});
        this.setState({ loadingMessage: '' });
      });
  };

  handleAzureAccountsChange = ({ azureSubscriptionIds = [] }) => {
    const { $hybridMap } = this.props;

    const diff = difference(this.cloudMapCollection.selectedSubscriptions, azureSubscriptionIds);
    const reverseDiff = difference(azureSubscriptionIds, this.cloudMapCollection.selectedSubscriptions);

    if ([...diff, ...reverseDiff].length === 0) {
      // no change in Subscription selection
      return;
    }

    $hybridMap.settingsModel.set('azureSubscriptionIds', azureSubscriptionIds);

    this.setState({ loadingMessage: 'Loading Azure Topology...' });
    this.cloudMapCollection
      .fetchConnectivityCheckerTopology({
        force: true,
        data: { subscriptionIds: azureSubscriptionIds }
      })
      .then(() => this.intializeDefaultValues())
      .then((defaultValues) => {
        this.connectivityReportModel.set(defaultValues ?? {});
        this.setState({ loadingMessage: '' });
      });
  };

  componentDidMount() {
    const { match, $clouds } = this.props;

    const cloud = match?.params?.cloud ?? 'aws';

    this.cloud = cloud;
    if (cloud === 'aws') {
      this.setState({ loadingMessage: 'Loading AWS Topology...' });
    }

    if (cloud === 'azure') {
      this.setState({ loadingMessage: 'Loading Azure Topology...' });
    }

    if (!this.cloudMapCollection) {
      throw new Error(`Unable to create pathfinder report for cloud ${cloud}`);
    }

    this.cloudMapCollection
      .fetchConnectivityCheckerTopology()
      .then(() => this.intializeDefaultValues())
      .then((defaultValues) => {
        this.connectivityReportModel = $clouds.connectivityReportCollection.forge(defaultValues ?? {});
        this.setState({ loadingMessage: '' });
      });
  }

  get fields() {
    const initialFields = {
      name: {
        label: 'Name',
        placeholder: 'Will be autogenerated based on source and destination if left blank.',
        defaultValue: ''
      },

      src: {
        rules: 'required',
        label: 'Source'
      },
      dst: {
        rules: 'required',
        label: 'Destination'
      },
      dst_port: {
        label: 'Port',
        rules: 'port|required',
        type: 'number',
        defaultValue: '80'
      },
      protocol: {
        label: 'Protocol',
        defaultValue: 'tcp',
        rules: 'required',
        options: [
          { value: 'tcp', label: 'TCP' },
          { value: 'udp', label: 'UDP' }
        ]
      }
    };

    // default to AWS cloud
    initialFields.src_type = AwsSourceFieldType;
    initialFields.dst_type = AwsTargetFieldType;

    if (this.cloud === 'azure') {
      initialFields.src_type = AzureSourceFieldType;
      initialFields.dst_type = AzureTargetFieldType;
    }

    return initialFields;
  }

  handleCreateReport = (form) => {
    const { history } = this.props;
    const { selectedTime } = this.state;
    const { startDate, endDate, lookbackSeconds } = selectedTime;

    let start;
    let end = new Date();
    if (!endDate) {
      const bufferInSeconds = Math.abs(lookbackSeconds);
      start = moment().subtract(bufferInSeconds, 'seconds').toDate();
    } else {
      start = new Date(startDate * 1000);
      end = new Date(endDate * 1000);
    }

    const reportValues = {
      ...form.getValues(),
      cloud: this.cloud,
      start_time: start.toISOString(),
      end_time: end.toISOString()
    };

    // default to -1 to match buildReport execution for all reports on empty
    if (!reportValues.dst_port) {
      reportValues.dst_port = -1;
    }

    getHashForObject(reportValues).then((hash) => {
      history.push(`/v4/cloud/pathfinder/${this.cloud}/${hash}`);
    });
  };

  handleOnTypeChange = (field, value) => {
    this.connectivityReportModel.set(field.name, value);
  };

  renderLabel({ id, name, Name, ...rest }, propsToRenderArr) {
    const labelProps = propsToRenderArr.filter((prop) => prop !== 'id');
    const idToRender = convertToReadableId(id, this.cloud);
    return (
      <Flex flexDirection="column" gap={1}>
        <Text as="div" fontWeight="bold">
          {idToRender}
        </Text>
        {labelProps.map((key) => {
          const keyArr = key.split('.');
          const formattedKey = keyArr[keyArr.length - 1];
          const value = get(rest, key);
          if (!value) {
            return null;
          }
          return (
            <Flex gap={1} key={`${id}-${formattedKey}`} muted>
              <Text as="div">{formattedKey}:</Text>
              <Text as="div">{value}</Text>
            </Flex>
          );
        })}
      </Flex>
    );
  }

  // we need handle to generate name field
  handleOnValueChange = (field, value, _previousValue, form) => {
    const { nameUpdatedManually } = this.state;
    const { src, dst } = form.getValues();

    const sourceId = convertToReadableId(src, this.cloud);
    const destinationId = convertToReadableId(dst, this.cloud);

    if (!nameUpdatedManually) {
      this.connectivityReportModel.set(field.name, value);
      this.connectivityReportModel.set('name', `Connectivity between ${sourceId} and ${destinationId}`);
    }
  };

  handleOnDropdownSearch = async (searchText, entityType) => {
    const { options: fieldOptions } = this.cloud === 'aws' ? AwsSourceFieldType : AzureSourceFieldType;
    const { labelKeys } = fieldOptions.find(({ value }) => value.toLowerCase() === entityType.toLowerCase());
    const entities = this.cloudMapCollection?.getEntitiesByType(entityType) ?? [];

    this.setState({ hasEndpointOptions: entities.length > 0 });

    const initialFieldOptions = entities.map((entity) => ({
      entity: pick(entity, labelKeys),
      labelKeys,
      value: entity.id,
      label: this.renderLabel(entity, labelKeys)
    }));

    if (!searchText) {
      return initialFieldOptions;
    }

    // get only options that include search text in one of label key
    return initialFieldOptions.filter(({ entity }) =>
      labelKeys.some((key) => entity[key]?.toLowerCase()?.includes(searchText.toLowerCase()))
    );
  };

  // slow down queries with huge metadata
  debouncedHandleOnDropdownSearch = asyncDebounce(this.handleOnDropdownSearch, 600);

  handleDateRangeChange = (selectedTime) => {
    this.setState({ selectedTime });
  };

  getSelectedTimeSummary() {
    const {
      selectedTime: { lookbackSeconds, startDate, endDate }
    } = this.state;

    if (!startDate && !endDate) {
      const hours = Math.round(Math.abs(lookbackSeconds) / 3600);
      return `Last ${hours}h`;
    }

    const start = moment.unix(startDate).format('MMM D, HH:mm');
    const end = moment.unix(endDate).format('MMM D, HH:mm');

    return `Custom: ${start} – ${end}`;
  }

  renderScopePanel() {
    if (!this.isAwsPage && !this.isAzurePage) {
      return null;
    }

    return (
      <Card mb={2} p={2}>
        <Flex alignItems="center" justifyContent="space-between">
          <Text fontWeight="heavy">Scope</Text>
          <Box>
            {this.isAwsPage && <MapSearchAwsSubscriptionsPanel onChange={this.handleAwsAccountsChange} />}
            {this.isAzurePage && <MapSearchAzureSubscriptionsPanel onChange={this.handleAzureAccountsChange} />}
          </Box>
        </Flex>
      </Card>
    );
  }

  render() {
    const { history } = this.props;
    const { loadingMessage, selectedTime, hasEndpointOptions } = this.state;
    const hasModel = !!this.connectivityReportModel;

    return (
      <Page
        title="Create Report"
        parentLinks={[{ link: `/v4/cloud/pathfinder/${this.cloud}`, label: 'Cloud Pathfinder' }]}
        gray
      >
        <Container minWidth={650}>
          {this.renderScopePanel()}
          <Suspense
            loading={loadingMessage.length}
            fallback={
              <Flex width="100%" flexDirection="column" justifyContent="center" alignItems="center" gap={1}>
                <Spinner size={24} />
                <Text>{loadingMessage}</Text>
              </Flex>
            }
          >
            <Card p={3}>
              <Text as="div" fontWeight="heavy" mb={1} fontSize={16}>
                Pathfinder Report Details
              </Text>
              <Text as="div" muted mb={2}>
                Choose your endpoints, time window, and traffic details to generate a focused pathfinder report.
              </Text>
              {!hasModel && <Box height={260} showSkeleton />}
              {hasModel && (
                <FormComponent fields={this.fields} options={options} model={this.connectivityReportModel}>
                  {({ form }) => (
                    <Flex flexDirection="column" gap={3}>
                      <Box>
                        <Field name="name" width={600} onChange={() => this.setState({ nameUpdatedManually: true })}>
                          <InputGroup />
                        </Field>
                        <Text muted small mt="4px">
                          If left blank, we’ll name it after your selected endpoints.
                        </Text>
                      </Box>

                      <Box border="1px solid #e6e8eb" borderRadius={4} p={2} bg="subnavBackground">
                        <Text fontWeight="bold" mb={1}>
                          Endpoints
                        </Text>
                        <Flex gap={2} alignItems="flex-end">
                          <Field name="src_type" onChange={this.handleOnTypeChange}>
                            <Select />
                          </Field>
                          <Box flex={3}>
                            <Field
                              name="src"
                              onQuery={(search) =>
                                this.debouncedHandleOnDropdownSearch(search, form.getValue('src_type'))
                              }
                              onChange={this.handleOnValueChange}
                            >
                              <Select menuWidth="100%" showFilter autoComplete exactMatch clearable={false} />
                            </Field>
                          </Box>
                        </Flex>

                        <Flex gap={2} alignItems="flex-end" mt={1}>
                          <Field name="dst_type" onChange={this.handleOnTypeChange}>
                            <Select />
                          </Field>
                          <Box flex={3}>
                            <Field
                              name="dst"
                              onQuery={(search) =>
                                this.debouncedHandleOnDropdownSearch(search, form.getValue('dst_type'))
                              }
                              onChange={this.handleOnValueChange}
                            >
                              <Select menuWidth="100%" showFilter autoComplete exactMatch clearable={false} />
                            </Field>
                          </Box>
                        </Flex>
                        {!hasEndpointOptions && (
                          <Text color="#d9822b" small mt="4px">
                            No endpoints available for this scope. Refresh topology or adjust filters to continue.
                          </Text>
                        )}

                        <Flex justifyContent="space-between" alignItems="center" mb={2}>
                          <Label fontWeight={600}>Time Range</Label>
                          <Flex alignItems="center" gap={1} flexWrap="wrap">
                            <LookbackDateRange
                              minimal
                              onChange={this.handleDateRangeChange}
                              lookbackSeconds={selectedTime.lookbackSeconds || 3600}
                              allowSingleDayRange
                              {...selectedTime}
                            />
                            <Tag minimal intent="primary">
                              {this.getSelectedTimeSummary()}
                            </Tag>
                          </Flex>
                        </Flex>
                      </Box>

                      <Box border="1px solid #e6e8eb" borderRadius={4} p={2} bg="subnavBackground">
                        <Text fontWeight="bold" mb={1}>
                          Traffic Details
                        </Text>

                        <Flex gap={2} mt={2} flexWrap="wrap">
                          <Box minWidth="220px" flex={1}>
                            <Field name="protocol">
                              <Select menuWidth="100%" showFilter autoComplete exactMatch clearable={false} />
                            </Field>
                          </Box>

                          <Box minWidth="220px" flex={1}>
                            <Field name="dst_port">
                              <InputGroup />
                            </Field>
                          </Box>
                        </Flex>
                      </Box>

                      <Flex alignItems="center" justifyContent="space-between" mt={1}>
                        <Button text="Cancel" onClick={() => history.goBack()} intent="danger" />
                        <SubmitButton intent="primary" onSubmit={this.handleCreateReport} text="Run" />
                      </Flex>
                    </Flex>
                  )}
                </FormComponent>
              )}
            </Card>
          </Suspense>
        </Container>
      </Page>
    );
  }
}
